import { Icon } from '@ant-design/compatible';
import { Button, Col, Divider, Row, Table, Tabs } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { ModalTitle, SectionSubTitle } from '../../../components/text/Titles';
import { getDatasetReport } from '../../../lib/api/dataset-api';
import { useGetProjectAndDataset } from '../../../lib/hooks';
import { DatasetVersionReport } from '../../../types/dataset';
import moment from 'moment';
import { DatasetReportGraphs } from './DatasetReportGraphs';
import { Spinner } from '../../../components/loading/Spinner';
import { ErrorText } from '../../../components/text/ErrorText';
const { TabPane } = Tabs;

type ReturnType = {
  timeCreated0: string;
  timeCreated1: string | null;
};

export const getReportDates = (selectedReportsSorted: DatasetVersionReport[]): ReturnType => {
  const timeCreated0 = selectedReportsSorted[0].timeCreated;
  const timeCreated1 = selectedReportsSorted[1]?.timeCreated;
  if (timeCreated1) {
    if (moment(timeCreated0).format('l') === moment(timeCreated1).format('l')) {
      return {
        timeCreated0: moment(timeCreated0).format('l hh:mm'),
        timeCreated1: moment(timeCreated1).format('l hh:mm'),
      };
    } else {
      return { timeCreated0: moment(timeCreated0).format('l'), timeCreated1: moment(timeCreated1).format('l') };
    }
  }
  return { timeCreated0: moment(timeCreated0).format('l'), timeCreated1: null };
};

const DatasetReport = () => {
  const { datasetId, projectId } = useParams() as { datasetId: string; projectId: string };
  const { project, dataset, datasetVersion, loading } = useGetProjectAndDataset(projectId, datasetId);

  const [reportsLoading, setReportsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [datasetReports, setDatasetReports] = useState<DatasetVersionReport[]>([]);
  const [selectedReports, setSelectedReports] = useState<DatasetVersionReport[]>([]);
  const [selectedReportsSorted, setSelectedReportsSorted] = useState<DatasetVersionReport[]>([]);

  const getData = useCallback(async () => {
    try {
      setReportsLoading(true);
      const report = await getDatasetReport(datasetId);
      setDatasetReports(report);
      setError(false);
    } catch (e) {
      setError(true);
      setDatasetReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, [datasetId]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (datasetVersion && datasetReports.length) {
      setSelectedReports([datasetReports[0]]);
    }
  }, [datasetVersion, datasetReports]);

  useEffect(() => {
    if (selectedReports.length === 2) {
      getReportDates(selectedReports);
      setSelectedReportsSorted(
        [...selectedReports].sort((a, b) =>
          new Date(a.timeCreated).getTime() > new Date(b.timeCreated).getTime() ? 1 : -1
        )
      );
    } else {
      setSelectedReportsSorted([...selectedReports]);
    }
  }, [selectedReports]);

  const selectVersion = (version: DatasetVersionReport) => {
    if (selectedReports.includes(version)) {
      setSelectedReports(prev => prev.filter(v => v !== version));
    } else {
      if (selectedReports.length < 2) {
        setSelectedReports(prev => [...prev, version]);
      } else if (selectedReports.length === 2) {
        setSelectedReports(prev => [prev[1], version]);
      } else {
        return false;
      }
    }
  };

  const createStatisticsData = () => {
    const data: any[] = [];
    const fileCountObj: any = { statistic: 'File count' };
    const uniqueTagObj: any = { statistic: 'Unique tags' };
    if (selectedReportsSorted.length)
      selectedReportsSorted.forEach((rep, i) => {
        fileCountObj[`count${i}`] = rep.file_count;
        uniqueTagObj[`count${i}`] = rep.tags.length;
      });
    data.push(fileCountObj);
    data.push(uniqueTagObj);
    return data;
  };

  return (
    <>
      <PageHeader
        back={{
          url: `/projects/${projectId}/datasets/${datasetId}/overview`,
          label: (project?.name || '') + ' - ' + (dataset?.name || ''),
        }}
        header={(project ? project.name + ': ' : '') + (dataset ? dataset.name : '') + ' - Report'}
        description="Dataset stats per version."
      />
      <Row className="my-3" style={{ height: 'calc(100vh - 280px)' }}>
        <Col
          span={9}
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '100%',
            overflowY: 'auto',
          }}>
          {error ? <ErrorText>Something went wrong.</ErrorText> : null}
          {reportsLoading ? <Spinner /> : null}
          {datasetReports.length
            ? datasetReports.map((report, i, array) => {
                const background = selectedReportsSorted.includes(report) ? '#f8f8f8' : '';
                return (
                  <div key={report.version_id} style={{ display: 'flex', flexDirection: 'column' }}>
                    <Row
                      style={{
                        width: '100%',
                        alignItems: 'center',
                        border: '1px solid #dedede',
                        background: background,
                      }}>
                      <Col span={18} style={{ padding: '0.7em' }}>
                        <SectionSubTitle>{moment(report.timeCreated).format('ll hh:mm:ss')}</SectionSubTitle>
                        {i === 0 ? <span className="mx-2">(current)</span> : null}
                        <p className="text-toolbox-descriptions font-light mb-0">{report.version_id}</p>
                      </Col>
                      <Col offset={1} span={4}>
                        <Button
                          onClick={() => {
                            selectVersion(report);
                          }}>
                          {selectedReportsSorted.length ? (
                            selectedReportsSorted.includes(report) ? (
                              <Icon type="minus" />
                            ) : (
                              <Icon type="plus" />
                            )
                          ) : (
                            <Icon type="arrow-right" />
                          )}
                        </Button>
                      </Col>
                    </Row>
                    {i !== array.length - 1 ? (
                      <Divider
                        type="vertical"
                        style={{ borderLeft: '1px solid #dedede', marginLeft: '2em', height: '30px' }}
                      />
                    ) : null}
                  </div>
                );
              })
            : null}
        </Col>
        <Col offset={1} span={14} className="pb-10">
          {reportsLoading ? (
            <div style={{ height: '100%', border: '1px solid #dedede' }}>
              <Spinner />
            </div>
          ) : (
            <div style={{ border: '1px solid #dedede' }}>
              <div style={{ borderBottom: '1px solid #dedede', padding: '1em' }}>
                <ModalTitle>
                  {!error && (
                    <>
                      {!loading && selectedReportsSorted.length === 2
                        ? `Comparing ${getReportDates(selectedReportsSorted).timeCreated0} with ${
                            getReportDates(selectedReportsSorted).timeCreated1
                          }`
                        : selectedReportsSorted.length === 1
                        ? `Version ${moment(selectedReportsSorted[0].timeCreated).format('l')}`
                        : 'Select a version'}
                    </>
                  )}
                </ModalTitle>
              </div>
              <div className="p-4" style={{ overflow: 'hidden' }}>
                {selectedReportsSorted.length > 0 ? (
                  <Tabs defaultActiveKey="1" style={{ height: '100%' }}>
                    <TabPane tab="Basic" key="1">
                      {selectedReportsSorted.length > 0 ? (
                        <>
                          <DatasetReportGraphs selectedReportsSorted={selectedReportsSorted} />
                        </>
                      ) : null}
                    </TabPane>
                    <TabPane tab="Extended info" key="2">
                      <div>
                        <Table
                          style={{ marginBottom: '2em' }}
                          dataSource={createStatisticsData()}
                          columns={[
                            {
                              title: 'Statistic',
                              dataIndex: 'statistic',
                              key: 'statistic',
                              render: (text: string, row: any) => <span>{row.statistic}</span>,
                            },
                            {
                              title: getReportDates(selectedReportsSorted).timeCreated0,
                              dataIndex: 'count0',
                              key: 'count0',
                              render: (text: string, row: any) => <span>{row.count0}</span>,
                            },
                            ...(selectedReportsSorted[1]
                              ? [
                                  {
                                    title: getReportDates(selectedReportsSorted).timeCreated1,
                                    dataIndex: 'count1',
                                    key: 'count1',
                                    render: (text: string, row: any) => <span>{row.count1}</span>,
                                  },
                                  {
                                    title: 'Difference',
                                    dataIndex: 'difference',
                                    key: 'difference',
                                    render: (text: string, row: any) => (
                                      <span>
                                        {((row.count1 - row.count0) as number) > 0 ? '+' : ''}
                                        {row.count1 - row.count0}
                                      </span>
                                    ),
                                  },
                                ]
                              : []),
                          ]}
                          size="small"
                          pagination={false}
                          scroll={{ y: 240 }}
                          rowKey={row => row.statistic}
                        />
                        <SectionSubTitle>Tags</SectionSubTitle>
                        <Table
                          style={{ marginTop: '1em' }}
                          dataSource={([
                            ...selectedReportsSorted[0].tags.map(tagCount => {
                              const matchingTag = selectedReportsSorted[1]?.tags.find(t => tagCount.tag === t.tag);
                              return {
                                ...tagCount,
                                count0: tagCount.count,
                                count1: matchingTag?.count || 0,
                              };
                            }),
                            ...(selectedReportsSorted[1]
                              ? selectedReportsSorted[1].tags
                                  .filter(t1 => !selectedReportsSorted[0].tags.find(t2 => t2.tag === t1.tag))
                                  .map(tagCount => {
                                    return {
                                      ...tagCount,
                                      count0: 0,
                                      count1: tagCount.count,
                                    };
                                  })
                              : []),
                          ] as { tag: string; count0: number; count1: null | number }[]).filter(
                            t => t.count0 - (t.count1 || 0) !== 0
                          )}
                          columns={[
                            {
                              title: 'Tag',
                              dataIndex: 'tag',
                              key: 'tag',
                              render: (text: string, tagCount: any) => <span>{tagCount.tag}</span>,
                            },
                            {
                              title: getReportDates(selectedReportsSorted).timeCreated0,
                              dataIndex: getReportDates(selectedReportsSorted).timeCreated0,
                              key: getReportDates(selectedReportsSorted).timeCreated0,
                              render: (text: string, tagCount: any) => <span>{tagCount.count0}</span>,
                            },
                            ...(selectedReportsSorted[1]
                              ? [
                                  {
                                    title: getReportDates(selectedReportsSorted).timeCreated1!,
                                    dataIndex: getReportDates(selectedReportsSorted).timeCreated1!,
                                    key: getReportDates(selectedReportsSorted).timeCreated1!,
                                    render: (text: string, tagCount: any) => <span>{tagCount.count1}</span>,
                                  },
                                  {
                                    title: 'Difference',
                                    dataIndex: 'difference',
                                    key: 'difference',
                                    render: (text: string, tagCount: any) => (
                                      <span>
                                        {tagCount.count1 - tagCount.count0 > 0 ? '+' : ''}
                                        {tagCount.count1 - tagCount.count0}
                                      </span>
                                    ),
                                  },
                                ]
                              : []),
                          ]}
                          size="small"
                          pagination={false}
                          scroll={{ y: 240 }}
                          rowKey={record => record.tag}
                        />
                      </div>
                    </TabPane>
                  </Tabs>
                ) : (
                  <p>No version selected</p>
                )}
              </div>
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

export default DatasetReport;
